import React from "react";
import { Box, Typography, Paper } from "@mui/material";

const fmt = (v) =>
  "₹" + new Intl.NumberFormat("en-IN").format(v || 0);

const DotBar = ({ verified, cap }) => {
  const capped = verified >= cap;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cap}, 1fr)`, gap: "3px", mt: 1 }}>
      {Array.from({ length: cap }).map((_, i) => {
        let color;
        if (capped) {
          color = i === cap - 1 ? "#f59e0b" : "#10B981";
        } else {
          color = i < verified ? "#10B981" : "#e2e8f0";
        }
        return <Box key={i} sx={{ height: 6, borderRadius: "999px", bgcolor: color }} />;
      })}
    </Box>
  );
};

const ProjectCard = ({ p }) => {
  const capped = p.rewarded_count >= p.max_visits_per_fos;
  const badge = capped
    ? { label: "⚠ CAP", color: "#92400e", bg: "rgba(251,191,36,.30)", border: "1px solid rgba(251,191,36,.40)" }
    : { label: `${p.remaining} LEFT`, color: "#3D4AD0", bg: "rgba(79,93,231,.12)", border: "1px solid rgba(79,93,231,.20)" };

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.5,
        border: capped ? "1px solid rgba(251,191,36,.40)" : "1px solid #e2e8f0",
        bgcolor: capped ? "rgba(255,251,235,.60)" : "#fff",
        transition: "transform .2s, box-shadow .2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(11,18,32,.08)" },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>{p.project_name}</Typography>
        <Box sx={{ fontSize: "10px", fontWeight: 700, color: badge.color, bgcolor: badge.bg, border: badge.border, borderRadius: "999px", px: 1, py: 0.25, whiteSpace: "nowrap", flexShrink: 0 }}>
          {badge.label}
        </Box>
      </Box>
      <Typography sx={{ fontSize: "11px", color: "#6B7488", mt: 0.25 }}>
        {p.rewarded_count}/{p.max_visits_per_fos} verified
      </Typography>
      <DotBar verified={p.rewarded_count} cap={p.max_visits_per_fos} />
      <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#047857", mt: 1 }}>
        {fmt(p.total_site_visit_earned)}
      </Typography>
    </Box>
  );
};

const SiteVisitTrackingTable = ({ siteVisitSummary = [], siteVisitCounts = null }) => {
  const totalVerified = siteVisitCounts?.total_verified ?? siteVisitSummary.reduce((s, p) => s + p.rewarded_count, 0);
  const totalEligible = siteVisitCounts?.total_credited ?? null;
  const totalEarned = siteVisitSummary.reduce((s, p) => s + p.total_site_visit_earned, 0);
  const potentialLeft = siteVisitSummary.reduce((s, p) => s + p.remaining * p.amount_per_visit, 0);
  const ratePerVisit = siteVisitSummary[0]?.amount_per_visit ?? 1500;

  return (
    <Paper sx={{ borderRadius: 3, p: 2.5, boxShadow: "0 1px 2px rgba(11,18,32,.04), 0 4px 12px rgba(11,18,32,.04)", border: "1px solid #e2e8f0" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>📍 Site visit tracking</Typography>
            <Box sx={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, bgcolor: "#eff2f8", color: "#6B7488", px: 1.5, py: 0.25, borderRadius: "999px" }}>
              Secondary incentive
            </Box>
          </Box>
          <Typography sx={{ fontSize: "12px", color: "#6B7488", mt: 0.25 }}>
            {fmt(ratePerVisit)} per verified visit · cap varies per project
          </Typography>
        </Box>
        {/* Legend */}
        <Box display="flex" alignItems="center" gap={2} sx={{ fontSize: "11px", color: "#474F60" }}>
          <Box display="flex" alignItems="center" gap={0.5}><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10B981" }} /> Verified</Box>
          <Box display="flex" alignItems="center" gap={0.5}><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} /> Cap reached</Box>
          <Box display="flex" alignItems="center" gap={0.5}><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#e2e8f0" }} /> Remaining</Box>
        </Box>
      </Box>

      {siteVisitSummary.length === 0 ? (
        /* Empty state */
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography sx={{ fontSize: "32px", mb: 1 }}>📍</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#474F60" }}>No site visits recorded yet</Typography>
          <Typography sx={{ fontSize: "12px", color: "#6B7488", mt: 0.5 }}>
            Verified site visits will appear here once allotted
          </Typography>
        </Box>
      ) : (
        <>
          {/* Summary stats */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1, mb: 2 }}>
            {[
              { label: "Verified", value: totalVerified, color: "#0B1220" },
              { label: "Eligible (paid)", value: totalEligible !== null ? totalEligible : "—", color: "#047857" },
              { label: "Earned", value: fmt(totalEarned), color: "#047857" },
              { label: "Potential left", value: fmt(potentialLeft), color: "#B45309" },
            ].map((s) => (
              <Box key={s.label} sx={{ bgcolor: "#f7f9fc", borderRadius: 1.5, px: 1.5, py: 1.25, textAlign: "center" }}>
                <Typography sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: 1, color: "#6B7488" }}>{s.label}</Typography>
                <Typography sx={{ fontSize: "18px", fontWeight: 800, color: s.color, fontVariantNumeric: "tabular-nums" }}>{s.value}</Typography>
              </Box>
            ))}
          </Box>

          {/* Project cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(5, 1fr)" }, gap: 1.5 }}>
            {siteVisitSummary.map((p) => (
              <ProjectCard key={p.project_name} p={p} />
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default SiteVisitTrackingTable;
